import { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Ban,
  Bot,
  Check,
  ChevronRight,
  CircleGauge,
  Clock3,
  FileCheck2,
  Gauge,
  History,
  LockKeyhole,
  Network,
  Play,
  ServerCog,
  ShieldCheck,
  X,
} from 'lucide-react';
import { deriveKpis, getPerson, type CompanySnapshot, type DomainEvent } from '@mk1/domain';
import type { ExportPreview, ProviderPool } from '@mk1/protocol';
import { Metric, Panel, PanelHeader, PersonaAvatar, ProvenanceBadge, StatusPill } from '@mk1/ui';

const toneFor = (value: string): 'cyan' | 'amber' | 'red' | 'green' | 'violet' | 'neutral' => {
  if (['BLOCKER', 'INCIDENT', 'BLOCKED', 'POLICY_BLOCKED'].includes(value)) return 'red';
  if (['ATTENTION', 'WAITING_HUMAN', 'AUTH_REQUIRED', 'LOW'].includes(value)) return 'amber';
  if (['AVAILABLE', 'HEALTHY', 'COMPLETED', 'QA_REVIEW'].includes(value)) return 'green';
  if (['WAITING_TOOL', 'COOLDOWN'].includes(value)) return 'violet';
  if (['DISABLED', 'UNKNOWN', 'OFFLINE'].includes(value)) return 'neutral';
  return 'cyan';
};

export function KpiStrip({ snapshot }: { snapshot: CompanySnapshot }) {
  const kpis = deriveKpis(snapshot);
  const metric = (name: string) => snapshot.operations.metrics.find((item) => item.name === name);
  return (
    <section className="kpi-strip" aria-label="Unternehmenskennzahlen">
      <Metric
        label="Active jobs"
        value={kpis.activeJobs}
        note={
          snapshot.source.mode === 'LIVE_LOCAL' ? 'database projection' : 'synthetic assignments'
        }
      />
      <Metric label="Human Gates" value={kpis.waitingApprovals} note="need Marvin" />
      <Metric label="Blocked" value={kpis.blockedJobs} note="top blocker below" />
      <Metric
        label="Human min"
        value={metric('human_minutes')?.value ?? kpis.humanMinutes}
        note={metric('human_minutes') ? 'exact synthetic record' : 'seeded baseline'}
      />
      <Metric label="Actual spend" value={`€${kpis.spendEur}`} note="€30 approved" />
      <Metric
        label="Queue SLA"
        value={
          metric('sla_met_ratio')
            ? `${Math.round(metric('sla_met_ratio')!.value * 100)}%`
            : kpis.queueSla
        }
        note={metric('sla_met_ratio') ? 'exact scenario result' : 'unknown until measured'}
      />
    </section>
  );
}

function ProviderCard({ pool, compact }: { pool: ProviderPool; compact: boolean }) {
  const statusCopy = pool.remainingLabel ?? 'Kein Messwert';
  const capacityWidth =
    pool.remainingUnit === 'PERCENT' && pool.remainingValue !== null
      ? Math.min(100, pool.remainingValue)
      : null;
  return (
    <article className="provider-card">
      <header>
        <span className="provider-mark">
          <Bot size={17} />
        </span>
        <div>
          <small>{pool.provider}</small>
          <strong>{pool.plan}</strong>
        </div>
        <StatusPill label={pool.status.replace('_', ' ')} tone={toneFor(pool.status)} />
      </header>
      <div className="capacity-readout">
        <div>
          <CircleGauge size={19} />
          <span>
            <small>Capacity</small>
            <strong>{statusCopy}</strong>
          </span>
        </div>
        <div>
          <Clock3 size={18} />
          <span>
            <small>Reset</small>
            <strong>{pool.resetLabel ?? 'unbekannt'}</strong>
          </span>
        </div>
      </div>
      <div
        className="capacity-bar"
        role="img"
        aria-label={
          pool.remainingValue === null
            ? 'Kapazität unbekannt'
            : `${pool.remainingValue} ${pool.remainingUnit}`
        }
      >
        {capacityWidth === null ? null : <i style={{ width: `${capacityWidth}%` }} />}
      </div>
      <footer>
        <span>{pool.tool}</span>
        <small>{pool.runnerLabel}</small>
        <ProvenanceBadge value={pool.provenance} compact />
      </footer>
      <p>{pool.policy}</p>
      {!compact ? (
        <dl className="provider-policy-facts">
          <div>
            <dt>Policy gate</dt>
            <dd>{pool.policyReviewStatus.replaceAll('_', ' ')}</dd>
          </div>
          <div>
            <dt>Observation</dt>
            <dd>
              {pool.capacitySource.replaceAll('_', ' ')} · {pool.confidence.replaceAll('_', ' ')}
            </dd>
          </div>
          <div>
            <dt>Weekly reset</dt>
            <dd>{pool.weeklyResetsAt ?? 'unknown'}</dd>
          </div>
          <div>
            <dt>Domains</dt>
            <dd>{pool.taskDomains.join(', ') || 'none'}</dd>
          </div>
        </dl>
      ) : null}
    </article>
  );
}

interface CapacityObservationDraft {
  poolId: string;
  status: 'AVAILABLE' | 'LOW' | 'EXHAUSTED' | 'COOLDOWN' | 'AUTH_REQUIRED';
  remainingValue: string;
  remainingUnit: 'MESSAGES' | 'CREDITS' | 'PERCENT' | 'UNKNOWN';
  resetsAt: string;
  weeklyResetsAt: string;
}

export function CapacityGrid({
  snapshot,
  compact = false,
  onNodeControl,
  onCapacityObservation,
}: {
  snapshot: CompanySnapshot;
  compact?: boolean;
  onNodeControl?: (nodeId: string, control: 'DRAIN' | 'QUARANTINE' | 'REVOKE') => Promise<unknown>;
  onCapacityObservation?: (input: {
    poolId: string;
    status: CapacityObservationDraft['status'];
    remainingValue: number | null;
    remainingUnit: CapacityObservationDraft['remainingUnit'];
    resetsAt: string | null;
    weeklyResetsAt: string | null;
  }) => Promise<unknown>;
}) {
  const observablePools = snapshot.providerPools.filter(
    (pool) => pool.adapterKey !== 'FAKE_JSONL' && pool.policyReviewStatus !== 'BLOCKED_BY_POLICY',
  );
  const firstPoolId = observablePools[0]?.id ?? '';
  const [observation, setObservation] = useState<CapacityObservationDraft>({
    poolId: firstPoolId,
    status: 'AVAILABLE',
    remainingValue: '',
    remainingUnit: 'UNKNOWN',
    resetsAt: '',
    weeklyResetsAt: '',
  });
  const [actionState, setActionState] = useState<string | null>(null);
  const cards = snapshot.providerPools.map((pool) => (
    <ProviderCard key={pool.id} pool={pool} compact={compact} />
  ));
  if (compact) return <div className="capacity-grid is-compact">{cards}</div>;
  const enrolledNodes = snapshot.operations.runners.filter(
    (runner) => runner.enrollmentStatus === 'ENROLLED' && runner.provenance !== 'SYNTHETIC_DEMO',
  );
  const connectedNodes = enrolledNodes.filter((runner) => runner.state === 'ONLINE');
  return (
    <Panel className="capacity-panel">
      <PanelHeader
        eyebrow="Observed capacity only"
        title="AI Capacity Grid"
        action={
          <StatusPill
            label={
              enrolledNodes.length
                ? `${connectedNodes.length} connected · ${enrolledNodes.length} enrolled`
                : '0 connected'
            }
            tone={connectedNodes.length ? 'violet' : 'neutral'}
          />
        }
      />
      <div className="capacity-grid">{cards}</div>
      {cards.length === 0 ? <p className="empty-copy">Keine Provider-Pools registriert.</p> : null}
      <div className="runner-grid-controls">
        <div>
          <span className="ui-eyebrow">Trusted runner nodes</span>
          <h3>Drain, quarantine or revoke</h3>
        </div>
        {enrolledNodes.length ? (
          enrolledNodes.map((runner) => (
            <article key={runner.id}>
              <span>
                <strong>{runner.label}</strong>
                <small>
                  {runner.id} · {runner.state} · {runner.adapterKeys.join(', ')}
                </small>
              </span>
              <div>
                {(['DRAIN', 'QUARANTINE', 'REVOKE'] as const).map((control) => (
                  <button
                    key={control}
                    className={
                      control === 'REVOKE'
                        ? 'button button--danger-outline'
                        : 'button button--ghost'
                    }
                    disabled={!onNodeControl || actionState !== null || runner.state === 'REVOKED'}
                    onClick={() => {
                      setActionState(`${runner.id}-${control}`);
                      void onNodeControl?.(runner.id, control).finally(() => setActionState(null));
                    }}
                  >
                    {actionState === `${runner.id}-${control}` ? 'Recording…' : control}
                  </button>
                ))}
              </div>
            </article>
          ))
        ) : (
          <p className="empty-copy">No real runner enrolled. Fake nodes remain separate.</p>
        )}
      </div>
      <form
        className="capacity-observation-form"
        onSubmit={(event) => {
          event.preventDefault();
          setActionState('capacity');
          const numeric = observation.remainingValue.trim()
            ? Number(observation.remainingValue)
            : null;
          void onCapacityObservation?.({
            poolId: observation.poolId,
            status: observation.status,
            remainingValue: numeric,
            remainingUnit: numeric === null ? 'UNKNOWN' : observation.remainingUnit,
            resetsAt: observation.resetsAt ? new Date(observation.resetsAt).toISOString() : null,
            weeklyResetsAt: observation.weeklyResetsAt
              ? new Date(observation.weeklyResetsAt).toISOString()
              : null,
          }).finally(() => setActionState(null));
        }}
      >
        <div>
          <span className="ui-eyebrow">Manual observation</span>
          <h3>Operator-confirmed capacity</h3>
          <small>No inferred precision · no automatic spend</small>
        </div>
        <label>
          Pool
          <select
            value={observation.poolId}
            onChange={(event) =>
              setObservation((current) => ({ ...current, poolId: event.target.value }))
            }
          >
            {observablePools.map((pool) => (
              <option key={pool.id} value={pool.id}>
                {pool.provider} · {pool.plan}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select
            value={observation.status}
            onChange={(event) =>
              setObservation((current) => ({
                ...current,
                status: event.target.value as CapacityObservationDraft['status'],
              }))
            }
          >
            {['AVAILABLE', 'LOW', 'EXHAUSTED', 'COOLDOWN', 'AUTH_REQUIRED'].map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </label>
        <label>
          Remaining (optional)
          <input
            type="number"
            min="0"
            value={observation.remainingValue}
            onChange={(event) =>
              setObservation((current) => ({ ...current, remainingValue: event.target.value }))
            }
          />
        </label>
        <label>
          Unit
          <select
            value={observation.remainingUnit}
            onChange={(event) =>
              setObservation((current) => ({
                ...current,
                remainingUnit: event.target.value as CapacityObservationDraft['remainingUnit'],
              }))
            }
          >
            {['UNKNOWN', 'MESSAGES', 'CREDITS', 'PERCENT'].map((unit) => (
              <option key={unit}>{unit}</option>
            ))}
          </select>
        </label>
        <label>
          Five-hour reset
          <input
            type="datetime-local"
            value={observation.resetsAt}
            onChange={(event) =>
              setObservation((current) => ({ ...current, resetsAt: event.target.value }))
            }
          />
        </label>
        <label>
          Weekly reset
          <input
            type="datetime-local"
            value={observation.weeklyResetsAt}
            onChange={(event) =>
              setObservation((current) => ({ ...current, weeklyResetsAt: event.target.value }))
            }
          />
        </label>
        <button
          className="button button--primary"
          disabled={!onCapacityObservation || actionState !== null}
        >
          {actionState === 'capacity' ? 'Recording…' : 'Record observation'}
        </button>
      </form>
      <div className="truth-note">
        <ShieldCheck size={16} />
        <span>
          Fake nodes are SYNTHETIC_DEMO only. Real adapters remain disabled until their guarded
          owner smoke gate; capacity never triggers paid fallback.
        </span>
      </div>
    </Panel>
  );
}

export function EventStream({
  events,
  visibleCount,
  running,
  onToggle,
}: {
  events: DomainEvent[];
  visibleCount: number;
  running: boolean;
  onToggle: () => void;
}) {
  const [correlationFilter, setCorrelationFilter] = useState('');
  const demo = events.some((event) => event.provenance === 'SYNTHETIC_DEMO');
  const filteredEvents = correlationFilter
    ? events.filter((event) => event.correlationId.includes(correlationFilter))
    : events;
  return (
    <Panel className="event-panel">
      <PanelHeader
        eyebrow={demo ? 'Deterministic simulator' : 'Append-only audit projection'}
        title={demo ? 'Live event stream' : 'Recorded event stream'}
        action={
          demo ? (
            <button
              className={running ? 'live-toggle is-running' : 'live-toggle'}
              onClick={onToggle}
            >
              <span />
              {running ? 'Pause' : 'Play demo'}
            </button>
          ) : (
            <StatusPill label="immutable" tone="green" />
          )
        }
      />
      <label className="event-filter">
        Correlation filter
        <input
          value={correlationFilter}
          onChange={(event) => setCorrelationFilter(event.target.value)}
          placeholder="CORR-SYNTHETIC…"
        />
      </label>
      <ol className="event-list" aria-live="polite">
        {filteredEvents.slice(0, visibleCount).map((event) => (
          <li key={event.id} className={`event event--${event.severity.toLowerCase()}`}>
            <span className="event__rail">
              <i />
            </span>
            <div>
              <time>
                {new Date(event.occurredAt).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </time>
              <strong>{event.type}</strong>
              <p>{event.message}</p>
              <small>
                {event.actorId} → {event.subjectId}
              </small>
            </div>
          </li>
        ))}
      </ol>
      {filteredEvents.length === 0 ? (
        <p className="empty-copy">Noch keine Audit-Events vorhanden.</p>
      ) : null}
      <footer className="event-footer">
        <ProvenanceBadge value={events[0]?.provenance ?? 'SEEDED'} compact />
        <span>
          {Math.min(visibleCount, filteredEvents.length)}/{filteredEvents.length}{' '}
          {demo ? 'deterministische Events' : 'aufgezeichnete Events'}
        </span>
      </footer>
    </Panel>
  );
}

export function ApprovalInbox({
  snapshot,
  onOpen,
}: {
  snapshot: CompanySnapshot;
  onOpen: (id: string) => void;
}) {
  const openGates = snapshot.approvals.filter((gate) =>
    ['PROPOSED', 'PENDING', 'APPROVED_ONCE', 'APPROVED_BOUNDED', 'RETURNED'].includes(gate.status),
  );
  return (
    <Panel className="approval-panel">
      <PanelHeader
        eyebrow="Human attention"
        title="Approval inbox"
        action={<span className="count-badge">{openGates.length}</span>}
      />
      <div className="approval-list">
        {openGates.map((gate) => (
          <button key={gate.id} className="approval-row" onClick={() => onOpen(gate.id)}>
            <span className="approval-icon">
              <LockKeyhole size={17} />
            </span>
            <span>
              <small>
                {gate.type} · {gate.risk}
              </small>
              <strong>{gate.title}</strong>
              <em>{gate.consequence}</em>
            </span>
            <span className="approval-expiry">
              <small>expires</small>
              <b>{gate.expiresLabel}</b>
              <ChevronRight size={16} />
            </span>
          </button>
        ))}
        {openGates.length === 0 ? <p className="empty-copy">Keine offenen Human Gates.</p> : null}
      </div>
    </Panel>
  );
}

export function GateDetail({
  snapshot,
  gateId,
  onClose,
  onDecision,
}: {
  snapshot: CompanySnapshot;
  gateId: string;
  onClose: () => void;
  onDecision?: (
    decision: 'APPROVE_ONCE' | 'APPROVE_BOUNDED' | 'REJECT' | 'RETURN' | 'REVOKE',
    input: { reason: string; scope?: Record<string, unknown>; expiresAt?: string },
  ) => Promise<unknown>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [boundedMinutes, setBoundedMinutes] = useState(30);
  const gate = snapshot.approvals.find((item) => item.id === gateId);
  if (!gate) return null;
  const requester = getPerson(snapshot, gate.requesterId);
  const approver = gate.approverId ? getPerson(snapshot, gate.approverId) : undefined;
  const affected = getPerson(snapshot, gate.affectedPersonId);
  return (
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside
        className="drawer gate-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gate-title"
      >
        <header className="drawer__hero gate-hero">
          <span className="gate-shield">
            <ShieldCheck size={23} />
          </span>
          <div>
            <span className="ui-eyebrow">
              {gate.type} · {gate.id}
            </span>
            <h2 id="gate-title">{gate.title}</h2>
            <p>Entscheidungspaket · {gate.status}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Human Gate schließen">
            <X size={19} />
          </button>
        </header>
        <div className="gate-riskbar">
          <StatusPill label={gate.risk} tone="amber" />
          <StatusPill label={gate.dataClass} tone="violet" />
          <span>
            Verfällt: <b>{gate.expiresLabel}</b>
          </span>
        </div>
        <div className="drawer__body gate-body">
          <section className="gate-section">
            <span className="ui-eyebrow">Exact requested action</span>
            <p className="gate-action">{gate.action}</p>
          </section>
          <section className="gate-people">
            {[
              ['Requester', requester],
              ['Affected', affected],
              ['Approver', approver],
            ].map(([label, person]) =>
              person && typeof person !== 'string' ? (
                <div key={label as string}>
                  <PersonaAvatar
                    name={person.name}
                    nature={person.nature}
                    status={person.status}
                    compact
                  />
                  <span>
                    <small>{label as string}</small>
                    <strong>{person.name}</strong>
                    <em>{person.id}</em>
                  </span>
                </div>
              ) : null,
            )}
          </section>
          <section className="consequence-card">
            <AlertTriangle size={20} />
            <span>
              <small>Consequence</small>
              <strong>{gate.consequence}</strong>
            </span>
          </section>
          <section className="gate-section">
            <span className="ui-eyebrow">Payload preview</span>
            <pre>{gate.payloadPreview}</pre>
            <small className="payload-hash">SHA-256 · {gate.payloadHash}</small>
          </section>
          <section className="gate-section">
            <span className="ui-eyebrow">Evidence packet</span>
            <ul>
              {gate.evidence.map((item) => (
                <li key={item}>
                  <FileCheck2 size={15} />
                  {item}
                </li>
              ))}
            </ul>
          </section>
          <section className="gate-decision-form">
            <label>
              Recorded decision reason
              <textarea
                rows={2}
                value={decisionReason}
                onChange={(event) => setDecisionReason(event.target.value)}
                placeholder="Why is this exact payload safe, or what must change?"
              />
            </label>
            <label>
              Bounded duration (minutes)
              <input
                type="number"
                min={5}
                max={240}
                value={boundedMinutes}
                onChange={(event) => setBoundedMinutes(Number(event.target.value))}
              />
            </label>
          </section>
          <section className="gate-actions" aria-label="Human-Gate-Aktionen">
            <button
              className="button button--ghost"
              disabled={!onDecision || submitting || decisionReason.trim().length < 8}
              onClick={() => {
                if (!onDecision) return;
                setSubmitting(true);
                setMutationError(null);
                void onDecision('RETURN', { reason: decisionReason.trim() })
                  .then(onClose)
                  .catch((failure: unknown) =>
                    setMutationError(
                      failure instanceof Error ? failure.message : 'Gate mutation failed.',
                    ),
                  )
                  .finally(() => setSubmitting(false));
              }}
            >
              Return
            </button>
            <button
              className="button button--ghost"
              disabled={!onDecision || submitting || decisionReason.trim().length < 8}
              onClick={() => {
                if (!onDecision) return;
                setSubmitting(true);
                setMutationError(null);
                void onDecision('REJECT', { reason: decisionReason.trim() })
                  .then(onClose)
                  .catch((failure: unknown) =>
                    setMutationError(
                      failure instanceof Error ? failure.message : 'Gate mutation failed.',
                    ),
                  )
                  .finally(() => setSubmitting(false));
              }}
            >
              Reject
            </button>
            <button
              className="button button--primary"
              disabled={
                !onDecision ||
                submitting ||
                gate.status !== 'PENDING' ||
                decisionReason.trim().length < 8
              }
              onClick={() => {
                if (!onDecision) return;
                setSubmitting(true);
                setMutationError(null);
                void onDecision('APPROVE_ONCE', { reason: decisionReason.trim() })
                  .then(onClose)
                  .catch((failure: unknown) =>
                    setMutationError(
                      failure instanceof Error ? failure.message : 'Gate mutation failed.',
                    ),
                  )
                  .finally(() => setSubmitting(false));
              }}
            >
              {submitting ? 'Recording…' : 'Approve once'}
            </button>
            <button
              className="button button--primary"
              disabled={
                !onDecision ||
                submitting ||
                gate.status !== 'PENDING' ||
                decisionReason.trim().length < 8
              }
              onClick={() => {
                if (!onDecision) return;
                const expiresAt = new Date(Date.now() + boundedMinutes * 60_000).toISOString();
                setSubmitting(true);
                setMutationError(null);
                void onDecision('APPROVE_BOUNDED', {
                  reason: decisionReason.trim(),
                  scope:
                    Object.keys(gate.scope).length > 0
                      ? gate.scope
                      : {
                          action: gate.action,
                          jobId: gate.jobId,
                          affectedPersonId: gate.affectedPersonId,
                        },
                  expiresAt,
                })
                  .then(onClose)
                  .catch((failure: unknown) =>
                    setMutationError(
                      failure instanceof Error ? failure.message : 'Gate mutation failed.',
                    ),
                  )
                  .finally(() => setSubmitting(false));
              }}
            >
              Approve bounded
            </button>
          </section>
          {mutationError ? <p className="form-error">{mutationError}</p> : null}
        </div>
        <footer className="drawer__footer">
          <ProvenanceBadge value={gate.provenance} />
          <span>
            {snapshot.source.mode === 'LIVE_LOCAL'
              ? 'Payload hash bound · approval is audited · no external action'
              : 'Preview only · approval is not persisted or executed'}
          </span>
        </footer>
      </aside>
    </div>
  );
}

const statusColumns = [
  {
    id: 'WAITING',
    label: 'Waiting / blocked',
    values: ['WAITING_HUMAN', 'WAITING_TOOL', 'BLOCKED'],
  },
  { id: 'ACTIVE', label: 'In production', values: ['ASSIGNED', 'RUNNING'] },
  { id: 'QA', label: 'Independent QA', values: ['QA_REVIEW'] },
  { id: 'QUEUE', label: 'Ready / queued', values: ['READY', 'QUEUED', 'WAITING_DEPENDENCY'] },
] as const;

export function WorkControl({ snapshot }: { snapshot: CompanySnapshot }) {
  const [mode, setMode] = useState<'kanban' | 'dependency' | 'table'>('kanban');
  return (
    <Panel className="surface-panel work-panel">
      <PanelHeader
        eyebrow="Jobs · queues · dependencies"
        title="Work Control"
        action={
          <div className="view-switch">
            <button
              className={mode === 'kanban' ? 'is-active' : ''}
              onClick={() => setMode('kanban')}
            >
              Kanban
            </button>
            <button
              className={mode === 'dependency' ? 'is-active' : ''}
              onClick={() => setMode('dependency')}
            >
              Dependencies
            </button>
            <button
              className={mode === 'table' ? 'is-active' : ''}
              onClick={() => setMode('table')}
            >
              Table
            </button>
          </div>
        }
      />
      <div className="queue-policy-strip" aria-label="Department queue policy">
        {snapshot.operations.queues.map((queue) => (
          <span key={queue.id}>
            <b>{queue.departmentId.replace('DEPT-', '')}</b>
            <small>
              {queue.itemIds.length} items · oldest{' '}
              {queue.oldestItemHours === null ? '—' : `${queue.oldestItemHours.toFixed(1)}h`}
            </small>
            <StatusPill
              label={queue.paused ? 'PAUSED' : 'ORDERED'}
              tone={queue.paused ? 'amber' : 'green'}
            />
          </span>
        ))}
      </div>
      {mode === 'kanban' ? (
        <div className="kanban">
          {statusColumns.map((column) => {
            const jobs = snapshot.jobs.filter((job) => column.values.includes(job.status as never));
            return (
              <section key={column.id}>
                <header>
                  <span>{column.label}</span>
                  <b>{jobs.length}</b>
                </header>
                <div>
                  {jobs.map((job) => (
                    <article className="job-card" key={job.id}>
                      <div>
                        <span className={`priority priority--${job.priority.toLowerCase()}`}>
                          {job.priority}
                        </span>
                        <StatusPill
                          label={job.status.replace('_', ' ')}
                          tone={toneFor(job.status)}
                        />
                      </div>
                      <strong>{job.title}</strong>
                      <small>{job.id}</small>
                      <p>{job.blockedReason ?? `Next: ${job.nextOwnerId ?? '—'}`}</p>
                      <footer>
                        <span>{job.ownerId}</span>
                        <span>
                          {job.risk} · {job.dataClass}
                        </span>
                      </footer>
                      <ProvenanceBadge value={job.provenance} compact />
                    </article>
                  ))}
                  {jobs.length === 0 ? (
                    <p className="empty-copy">Keine Jobs in diesem Zustand.</p>
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      ) : mode === 'dependency' ? (
        <div className="dependency-graph" aria-label="Job dependency graph">
          {snapshot.operations.dependencies.map((dependency) => {
            const predecessor = snapshot.jobs.find(
              (item) => item.id === dependency.predecessorJobId,
            );
            const successor = snapshot.jobs.find((item) => item.id === dependency.successorJobId);
            return (
              <article key={dependency.id}>
                <div>
                  <small>Predecessor</small>
                  <strong>{predecessor?.title ?? dependency.predecessorJobId}</strong>
                  <StatusPill label={predecessor?.status ?? 'UNKNOWN'} tone="neutral" />
                </div>
                <span className="dependency-arrow">
                  <Network size={18} />
                  <b>{dependency.condition}</b>
                  <StatusPill label={dependency.status} tone={toneFor(dependency.status)} />
                </span>
                <div>
                  <small>Successor</small>
                  <strong>{successor?.title ?? dependency.successorJobId}</strong>
                  <p>{dependency.blockedReason ?? 'Dependency satisfied; claim permitted.'}</p>
                </div>
              </article>
            );
          })}
          {snapshot.operations.dependencies.length === 0 ? (
            <p className="empty-copy">No durable dependency graph has been recorded yet.</p>
          ) : null}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="dense-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Risk</th>
                <th>Next owner / reason</th>
                <th>Deadline / retries</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    <strong>{job.title}</strong>
                    <small>{job.id}</small>
                  </td>
                  <td>{job.ownerId}</td>
                  <td>
                    <StatusPill label={job.status.replace('_', ' ')} tone={toneFor(job.status)} />
                  </td>
                  <td>{job.risk}</td>
                  <td>{job.blockedReason ?? job.nextOwnerId}</td>
                  <td>
                    {new Date(job.deadline).toLocaleString('de-DE', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                    <small>
                      retries {job.retryCount}/{job.retryLimit}
                    </small>
                  </td>
                  <td>
                    <ProvenanceBadge value={job.provenance} compact />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="dependency-rail">
        <Network size={18} />
        <span>
          <b>Handoff chain</b> Nora scope → Jonas production → Leila QA → Marvin Human Gate
        </span>
        <StatusPill
          label={snapshot.operations.companyDay.status.replace('_', ' ')}
          tone={snapshot.operations.companyDay.status === 'COMPLETED' ? 'green' : 'violet'}
        />
      </div>
    </Panel>
  );
}

export function OperationsOverview({
  snapshot,
  running,
  onRun,
}: {
  snapshot: CompanySnapshot;
  running: boolean;
  onRun?: () => Promise<void>;
}) {
  const run = snapshot.operations.companyDay;
  const activeIncidents = snapshot.operations.incidents.filter(
    (item) => item.status !== 'RESOLVED',
  );
  const chaosCases = [
    ['Heartbeat lost', 'RUNNER.HEARTBEAT_LOST'],
    ['Duplicate event', 'EVENT.DUPLICATE_IGNORED'],
    ['Lease expired', 'TOOL_LEASE.EXPIRED'],
    ['Dependency cancelled', 'DEPENDENCY.CANCELLED'],
    ['Queue paused', 'QUEUE.PAUSED'],
    ['Quota exhausted', 'CAPACITY.EXHAUSTED'],
    ['Stale approval', 'GATE.STALE_REJECTED'],
    ['Audit unavailable', 'AUDIT.STORE_UNAVAILABLE'],
    ['Employee suspended', 'PERSON.SUSPENDED'],
  ] as const;
  return (
    <Panel className="operations-panel">
      <PanelHeader
        eyebrow="SYNTHETIC_DEMO · event-backed operations"
        title="Live operations loop"
        action={
          <StatusPill
            label={run.status.replace('_', ' ')}
            tone={run.status === 'COMPLETED' ? 'green' : run.status === 'FAILED' ? 'red' : 'violet'}
          />
        }
      />
      <div className="operations-summary">
        <div>
          <ServerCog size={20} />
          <span>
            <small>Fake runner nodes</small>
            <strong>{snapshot.operations.runners.length}</strong>
            <em>No provider process</em>
          </span>
        </div>
        <div>
          <Activity size={20} />
          <span>
            <small>Scenario progress</small>
            <strong>
              {run.currentStep}/{run.totalSteps}
            </strong>
            <em>SSE updates only when events arrive</em>
          </span>
        </div>
        <div>
          <AlertTriangle size={20} />
          <span>
            <small>Open incidents</small>
            <strong>{activeIncidents.length}</strong>
            <em>{snapshot.operations.incidents.length} reconstructable total</em>
          </span>
        </div>
      </div>
      <div className="operations-runners">
        {snapshot.operations.runners.map((runner) => (
          <article key={runner.id}>
            <span className={runner.heartbeatFresh ? 'runner-dot is-fresh' : 'runner-dot'} />
            <div>
              <strong>{runner.label}</strong>
              <small>
                {runner.id} · {runner.trustClass}
              </small>
            </div>
            <StatusPill label={runner.state} tone={toneFor(runner.state)} />
            <ProvenanceBadge value={runner.provenance} compact />
          </article>
        ))}
      </div>
      <div className="chaos-grid" aria-label="Fail-safe chaos cases">
        {chaosCases.map(([label, eventType]) => {
          const recorded = snapshot.events.some((event) => event.type === eventType);
          return (
            <span key={eventType}>
              <Check size={14} />
              <b>{label}</b>
              <small>{recorded ? 'fail-safe recorded' : 'not run'}</small>
              <StatusPill
                label={recorded ? 'SAFE' : 'WAITING'}
                tone={recorded ? 'green' : 'neutral'}
              />
            </span>
          );
        })}
      </div>
      {snapshot.operations.incidents.length ? (
        <div className="incident-list" aria-label="Operational incidents">
          {snapshot.operations.incidents.map((incident) => (
            <article key={incident.id}>
              <AlertTriangle size={16} />
              <span>
                <strong>{incident.type}</strong>
                <small>{incident.impact}</small>
                <em>
                  Next owner: {incident.ownerId} · {incident.resolution ?? incident.containment}
                </em>
              </span>
              <StatusPill label={incident.status} tone={toneFor(incident.status)} />
            </article>
          ))}
        </div>
      ) : null}
      <div className="operations-actions">
        <p>
          Nora → Jonas → Leila → Marvin, including one correction, stale approval invalidation,
          one-time gate consumption and all fail-safe chaos cases.
        </p>
        <button
          className="button button--primary"
          disabled={!onRun || running || run.status === 'COMPLETED'}
          onClick={() => onRun && void onRun()}
        >
          <Play size={15} />
          {running
            ? 'Running synthetic day…'
            : run.status === 'COMPLETED'
              ? 'Company day complete'
              : 'Run company day'}
        </button>
      </div>
    </Panel>
  );
}

export function LegacyAccessControl({
  snapshot,
  onPerson,
}: {
  snapshot: CompanySnapshot;
  onPerson: (id: string) => void;
}) {
  const levels = ['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6'];
  return (
    <Panel className="surface-panel access-panel">
      <PanelHeader
        eyebrow="Authority preview · no implicit inheritance"
        title="Access Control"
        action={<StatusPill label="server-authorized" tone="green" />}
      />
      <div className="access-summary">
        <div>
          <ShieldCheck size={20} />
          <span>
            <strong>A0–A6 truth view</strong>
            <small>A4–A6 remain human-gated; no AI has external, secret or spend authority.</small>
          </span>
        </div>
        <div>
          <Ban size={20} />
          <span>
            <strong>Revocation preview</strong>
            <small>Open AIR-005 to simulate one local tool-lease revoke.</small>
          </span>
        </div>
      </div>
      <div className="table-wrap">
        <table className="access-table">
          <thead>
            <tr>
              <th>Person</th>
              {levels.map((level) => (
                <th key={level}>{level}</th>
              ))}
              <th>Review</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.people.map((person) => (
              <tr key={person.id}>
                <td>
                  <button className="person-cell" onClick={() => onPerson(person.id)}>
                    <PersonaAvatar
                      name={person.name}
                      nature={person.nature}
                      status={person.status}
                      compact
                    />
                    <span>
                      <strong>{person.name}</strong>
                      <small>
                        {person.nature === 'PERMANENT_AI' ? 'KI-Persona' : person.nature}
                      </small>
                    </span>
                  </button>
                </td>
                {levels.map((level) => {
                  const active = person.accessLevels.includes(level as never);
                  return (
                    <td key={level}>
                      <span
                        className={active ? 'access-dot is-active' : 'access-dot'}
                        aria-label={`${level} ${active ? 'aktiv' : 'nicht aktiv'}`}
                      >
                        {active ? <Check size={13} /> : '—'}
                      </span>
                    </td>
                  );
                })}
                <td>
                  <small>Day 15</small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="access-legend">
        <span>
          <i className="access-dot is-active">
            <Check size={11} />
          </i>{' '}
          standing or recorded bounded access
        </span>
        <span>
          <i className="access-dot">—</i> no standing grant
        </span>
        <span>
          <LockKeyhole size={14} /> A4–A6 require exact human scope
        </span>
      </div>
    </Panel>
  );
}

export function AccessControl({
  snapshot,
  onPerson,
  operatorRole,
  onRevokeGrant,
  onRevokeResource,
  onSuspendPerson,
  onPauseDepartment,
}: {
  snapshot: CompanySnapshot;
  onPerson: (id: string) => void;
  operatorRole?: 'OWNER' | 'BOUNDED_OPERATOR';
  onRevokeGrant?: (id: string, reason: string) => Promise<unknown>;
  onRevokeResource?: (id: string, reason: string) => Promise<unknown>;
  onSuspendPerson?: (id: string, reason: string) => Promise<unknown>;
  onPauseDepartment?: (id: string, reason: string) => Promise<unknown>;
}) {
  const levels = ['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6'];
  const [selectedPersonId, setSelectedPersonId] = useState(snapshot.people[0]?.id ?? '');
  const [selectedResourceId, setSelectedResourceId] = useState(
    snapshot.accessControl.resources[0]?.id ?? '',
  );
  const [confirmation, setConfirmation] = useState<{
    title: string;
    consequence: string;
    reversible: string;
    execute: (reason: string) => Promise<unknown>;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedPerson = snapshot.people.find((person) => person.id === selectedPersonId);
  const selectedResource = snapshot.accessControl.resources.find(
    (resource) => resource.id === selectedResourceId,
  );
  const selectedPersonGrants = snapshot.grants.filter(
    (grant) => grant.personId === selectedPersonId,
  );
  const selectedResourceGrants = snapshot.grants.filter(
    (grant) => grant.resourceId === selectedResourceId,
  );
  const canControl = operatorRole !== 'BOUNDED_OPERATOR';

  const openConfirmation = (
    title: string,
    consequence: string,
    reversible: string,
    execute: (value: string) => Promise<unknown>,
  ) => {
    setReason('');
    setError(null);
    setConfirmation({ title, consequence, reversible, execute });
  };

  return (
    <Panel className="surface-panel access-panel access-panel--operational">
      <PanelHeader
        eyebrow={`Policy v${snapshot.accessControl.policyVersion} · no implicit inheritance`}
        title="Access Control"
        action={
          <StatusPill
            label={
              operatorRole === 'BOUNDED_OPERATOR' ? 'HUM-002 bounded view' : 'server-authorized'
            }
            tone={operatorRole === 'BOUNDED_OPERATOR' ? 'amber' : 'green'}
          />
        }
      />
      <div className="access-summary">
        <div>
          <ShieldCheck size={20} />
          <span>
            <strong>A0–A6 decision truth</strong>
            <small>
              {snapshot.accessControl.policies.length} versioned boundaries · A4–A6 remain
              human-gated.
            </small>
          </span>
        </div>
        <div>
          <Ban size={20} />
          <span>
            <strong>{snapshot.accessControl.expiringGrantIds.length} expiring grants</strong>
            <small>Revocation stops leases, contains jobs and quarantines uncertain output.</small>
          </span>
        </div>
      </div>
      <div className="table-wrap">
        <table className="access-table">
          <thead>
            <tr>
              <th>Person</th>
              {levels.map((level) => (
                <th key={level}>{level}</th>
              ))}
              <th>Review</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.people.map((person) => (
              <tr key={person.id}>
                <td>
                  <button
                    className={
                      selectedPersonId === person.id ? 'person-cell is-selected' : 'person-cell'
                    }
                    onClick={() => setSelectedPersonId(person.id)}
                    onDoubleClick={() => onPerson(person.id)}
                  >
                    <PersonaAvatar
                      name={person.name}
                      nature={person.nature}
                      status={person.status}
                      compact
                    />
                    <span>
                      <strong>{person.name}</strong>
                      <small>
                        {person.id} · {person.status}
                      </small>
                    </span>
                  </button>
                </td>
                {levels.map((level) => {
                  const grants = snapshot.grants.filter(
                    (grant) => grant.personId === person.id && grant.accessLevel === level,
                  );
                  const state =
                    grants.find((grant) =>
                      ['ACTIVE', 'ACTIVE_BOUNDED', 'EXPIRING'].includes(grant.status),
                    )?.status ??
                    grants[0]?.status ??
                    'NONE';
                  const active = ['ACTIVE', 'ACTIVE_BOUNDED', 'EXPIRING'].includes(state);
                  return (
                    <td key={level}>
                      <span
                        className={active ? 'access-dot is-active' : 'access-dot'}
                        aria-label={`${level} ${state}`}
                        title={`${level}: ${state}`}
                      >
                        {active ? <Check size={13} /> : state === 'NONE' ? '—' : state.slice(0, 1)}
                      </span>
                    </td>
                  );
                })}
                <td>
                  <small>
                    {snapshot.accessControl.expiringGrantIds.some((id) =>
                      snapshot.grants.some(
                        (grant) => grant.id === id && grant.personId === person.id,
                      ),
                    )
                      ? '≤72h'
                      : 'bounded'}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="access-explorer">
        <section>
          <span className="ui-eyebrow">Person explorer</span>
          <div className="explorer-heading">
            <div>
              <strong>{selectedPerson?.name ?? 'Select a person'}</strong>
              <small>
                {selectedPerson?.id ?? '—'} · {selectedPerson?.status ?? '—'}
              </small>
            </div>
            {selectedPerson ? (
              <button
                className="button button--ghost button--small"
                onClick={() => onPerson(selectedPerson.id)}
              >
                Open profile
              </button>
            ) : null}
          </div>
          <div className="grant-stack">
            {selectedPersonGrants.map((grant) => (
              <article key={grant.id}>
                <div>
                  <StatusPill label={grant.status} tone={toneFor(grant.status)} />
                  <small>
                    {grant.accessLevel} · {grant.permission}
                  </small>
                </div>
                <strong>{grant.resource}</strong>
                <p>{grant.purpose}</p>
                <footer>
                  <span>expires {new Date(grant.expiry).toLocaleString('de-DE')}</span>
                  {canControl &&
                  ['ACTIVE', 'ACTIVE_BOUNDED', 'EXPIRING'].includes(grant.status) &&
                  onRevokeGrant ? (
                    <button
                      className="button button--danger button--small"
                      onClick={() =>
                        openConfirmation(
                          'Revoke this tool',
                          `Immediately revoke ${grant.permission} on ${grant.resource}; active leases stop, jobs wait and uncertain output is quarantined.`,
                          'The revoke is permanent. Work can continue only through a new independently approved grant.',
                          (value) => onRevokeGrant(grant.id, value),
                        )
                      }
                    >
                      Revoke this tool
                    </button>
                  ) : null}
                </footer>
              </article>
            ))}
            {selectedPersonGrants.length === 0 ? (
              <p className="empty-copy">No visible grants.</p>
            ) : null}
          </div>
          {canControl && selectedPerson && onSuspendPerson ? (
            <button
              className="button button--danger"
              disabled={selectedPerson.nature === 'HUMAN' || selectedPerson.status === 'SUSPENDED'}
              onClick={() =>
                openConfirmation(
                  'Suspend employee',
                  `Stop assignments and claims for ${selectedPerson.name}, revoke leases, quarantine uncertain output and offer safe transfer.`,
                  'The persona and history remain; restoring authority needs a separately audited decision.',
                  (value) => onSuspendPerson(selectedPerson.id, value),
                )
              }
            >
              Suspend employee
            </button>
          ) : null}
        </section>
        <section>
          <span className="ui-eyebrow">Resource explorer</span>
          <select
            value={selectedResourceId}
            onChange={(event) => setSelectedResourceId(event.target.value)}
          >
            {snapshot.accessControl.resources.map((resource) => (
              <option value={resource.id} key={resource.id}>
                {resource.type} · {resource.label}
              </option>
            ))}
          </select>
          {selectedResource ? (
            <div className="resource-summary-card">
              <div>
                <StatusPill
                  label={selectedResource.ownership}
                  tone={selectedResource.ownership === 'UNKNOWN' ? 'amber' : 'green'}
                />
                <StatusPill label={selectedResource.usage} tone="neutral" />
              </div>
              <strong>{selectedResource.label}</strong>
              <p>{selectedResource.notes}</p>
              <small>{selectedResource.evidenceRef}</small>
              <span>Secret reference: {selectedResource.secretReferenceState}</span>
              {canControl &&
              onRevokeResource &&
              selectedResourceGrants.some((grant) =>
                ['ACTIVE', 'ACTIVE_BOUNDED', 'EXPIRING'].includes(grant.status),
              ) ? (
                <button
                  className="button button--danger"
                  onClick={() =>
                    openConfirmation(
                      'Revoke resource access',
                      `Revoke every active grant and lease bound to ${selectedResource.label}; affected work is contained.`,
                      'The asset record remains. New access needs new purpose-bound grants.',
                      (value) => onRevokeResource(selectedResource.id, value),
                    )
                  }
                >
                  Revoke resource
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="expiry-dashboard">
            <strong>Expiry dashboard</strong>
            {snapshot.grants
              .toSorted((a, b) => Date.parse(a.expiry) - Date.parse(b.expiry))
              .slice(0, 5)
              .map((grant) => (
                <span key={grant.id}>
                  <b>{grant.id}</b>
                  <small>
                    {grant.personId} · {new Date(grant.expiry).toLocaleDateString('de-DE')}
                  </small>
                  <StatusPill label={grant.status} tone={toneFor(grant.status)} />
                </span>
              ))}
          </div>
        </section>
      </div>
      {canControl && onPauseDepartment ? (
        <div className="department-controls">
          <span>
            <b>Department containment</b>
            <small>Stops new claims; audit and containment stay available.</small>
          </span>
          {snapshot.departments.map((department) => (
            <button
              key={department.id}
              className="button button--ghost button--small"
              onClick={() =>
                openConfirmation(
                  `Pause ${department.shortName}`,
                  'New queue claims stop for this department. Read-only inspection and containment remain available.',
                  'Department execution can be resumed through a separate recorded control.',
                  (value) => onPauseDepartment(department.id, value),
                )
              }
            >
              Pause {department.shortName}
            </button>
          ))}
        </div>
      ) : null}
      {confirmation ? (
        <section
          className="critical-confirmation"
          role="alertdialog"
          aria-labelledby="critical-action-title"
        >
          <AlertTriangle size={21} />
          <div>
            <span className="ui-eyebrow">Deliberate critical action</span>
            <h3 id="critical-action-title">{confirmation.title}</h3>
            <p>{confirmation.consequence}</p>
            <small>{confirmation.reversible}</small>
            <label>
              Recorded reason
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
              />
            </label>
            <div>
              <button className="button button--ghost" onClick={() => setConfirmation(null)}>
                Cancel
              </button>
              <button
                className="button button--danger"
                disabled={reason.trim().length < 8 || submitting}
                onClick={() => {
                  setSubmitting(true);
                  setError(null);
                  void confirmation
                    .execute(reason.trim())
                    .then(() => setConfirmation(null))
                    .catch((failure: unknown) =>
                      setError(failure instanceof Error ? failure.message : 'Control failed.'),
                    )
                    .finally(() => setSubmitting(false));
                }}
              >
                {submitting ? 'Recording…' : `Confirm ${confirmation.title}`}
              </button>
            </div>
            {error ? <p className="form-error">{error}</p> : null}
          </div>
        </section>
      ) : null}
      <div className="access-legend">
        <span>
          <i className="access-dot is-active">
            <Check size={11} />
          </i>{' '}
          active or bounded grant
        </span>
        <span>
          <i className="access-dot">—</i> no standing grant
        </span>
        <span>
          <LockKeyhole size={14} /> A4–A6 require exact human scope
        </span>
      </div>
    </Panel>
  );
}

export function AssetRegistry({
  snapshot,
  onRevokeResource,
}: {
  snapshot: CompanySnapshot;
  onRevokeResource?: (id: string, reason: string) => Promise<unknown>;
}) {
  const [filter, setFilter] = useState('ALL');
  const resources = snapshot.accessControl.resources.filter(
    (resource) => filter === 'ALL' || resource.type === filter,
  );
  const types = [...new Set(snapshot.accessControl.resources.map((resource) => resource.type))];
  return (
    <Panel className="surface-panel asset-registry">
      <PanelHeader
        eyebrow="Authorized metadata · secret references redacted"
        title="Asset & Account Registry"
        action={<StatusPill label={`${resources.length} records`} tone="cyan" />}
      />
      <div className="asset-toolbar">
        <label>
          Asset type
          <select value={filter} onChange={(event) => setFilter(event.target.value)}>
            <option value="ALL">All types</option>
            {types.map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
        </label>
        <div className="truth-note">
          <ShieldCheck size={16} />
          <span>
            No ordinary form accepts or returns passwords, provider tokens, cookies or key material.
          </span>
        </div>
      </div>
      <div className="asset-grid">
        {resources.map((resource) => {
          const grants = snapshot.grants.filter((grant) => grant.resourceId === resource.id);
          return (
            <article key={resource.id}>
              <header>
                <span className="ui-eyebrow">
                  {resource.type} · {resource.id}
                </span>
                <StatusPill
                  label={resource.ownership}
                  tone={resource.ownership === 'UNKNOWN' ? 'amber' : 'green'}
                />
              </header>
              <h3>{resource.label}</h3>
              <p>{resource.notes}</p>
              <dl>
                <div>
                  <dt>Usage</dt>
                  <dd>{resource.usage}</dd>
                </div>
                <div>
                  <dt>Class</dt>
                  <dd>{resource.classification}</dd>
                </div>
                <div>
                  <dt>Secret ref</dt>
                  <dd>{resource.secretReferenceState}</dd>
                </div>
                <div>
                  <dt>Active grants</dt>
                  <dd>
                    {
                      grants.filter((grant) =>
                        ['ACTIVE', 'ACTIVE_BOUNDED', 'EXPIRING'].includes(grant.status),
                      ).length
                    }
                  </dd>
                </div>
              </dl>
              <small>{resource.evidenceRef}</small>
              {onRevokeResource &&
              grants.some((grant) =>
                ['ACTIVE', 'ACTIVE_BOUNDED', 'EXPIRING'].includes(grant.status),
              ) ? (
                <button
                  className="button button--danger button--small"
                  onClick={() =>
                    void onRevokeResource(
                      resource.id,
                      `Registry revoke for ${resource.label}; active grants require containment.`,
                    )
                  }
                >
                  Revoke resource access
                </button>
              ) : null}
            </article>
          );
        })}
      </div>
    </Panel>
  );
}

export function BottleneckPanel({ snapshot }: { snapshot: CompanySnapshot }) {
  const max = Math.max(1, ...snapshot.departments.map((department) => department.oldestItemHours));
  return (
    <Panel className="bottleneck-panel">
      <PanelHeader
        eyebrow="Oldest queue item"
        title="Department bottlenecks"
        action={<Gauge size={18} />}
      />
      <div className="bottlenecks">
        {snapshot.departments
          .toSorted((a, b) => b.oldestItemHours - a.oldestItemHours)
          .slice(0, 5)
          .map((department) => (
            <div key={department.id}>
              <span>
                <strong>{department.shortName}</strong>
                <small>{department.queueCount} queued</small>
              </span>
              <i>
                <b style={{ width: `${(department.oldestItemHours / max) * 100}%` }} />
              </i>
              <em>{department.oldestItemHours.toLocaleString('de-DE')}h</em>
            </div>
          ))}
      </div>
    </Panel>
  );
}

export function NotificationsPanel({
  snapshot,
  onAcknowledge,
}: {
  snapshot: CompanySnapshot;
  onAcknowledge?: (id: string) => Promise<unknown>;
}) {
  return (
    <Panel className="surface-panel notifications-panel">
      <PanelHeader
        eyebrow="Recorded signals"
        title="Notifications & evidence"
        action={<History size={18} />}
      />
      <div className="notification-grid">
        {snapshot.notifications.map((note) => (
          <article key={note.id}>
            <span className={`note-icon note-icon--${note.severity.toLowerCase()}`}>
              {note.severity === 'BLOCKER' ? <AlertTriangle size={18} /> : <Activity size={18} />}
            </span>
            <div>
              <StatusPill label={note.severity} tone={toneFor(note.severity)} />
              <h3>{note.title}</h3>
              <p>{note.detail}</p>
              <button
                disabled={note.acknowledgedAt !== null || !onAcknowledge}
                onClick={() => {
                  if (onAcknowledge) void onAcknowledge(note.id);
                }}
              >
                {note.acknowledgedAt ? 'Acknowledged' : note.nextAction}
                <ArrowUpRight size={14} />
              </button>
            </div>
          </article>
        ))}
        {snapshot.notifications.length === 0 ? (
          <p className="empty-copy">Keine Benachrichtigungen.</p>
        ) : null}
      </div>
    </Panel>
  );
}

export function ExportPreviewPanel({
  onPreview,
}: {
  onPreview?: (format: 'markdown' | 'json') => Promise<ExportPreview>;
}) {
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = (format: 'markdown' | 'json') => {
    if (!onPreview) return;
    setLoading(true);
    setError(null);
    void onPreview(format)
      .then(setPreview)
      .catch((failure: unknown) =>
        setError(failure instanceof Error ? failure.message : 'Export preview failed.'),
      )
      .finally(() => setLoading(false));
  };
  return (
    <Panel className="export-preview-panel">
      <PanelHeader
        eyebrow="Deterministic · read only"
        title="Durable record export preview"
        action={<StatusPill label="no file writes" tone="green" />}
      />
      <div className="export-preview-actions">
        <button
          className="button button--ghost"
          disabled={!onPreview || loading}
          onClick={() => load('markdown')}
        >
          Markdown preview
        </button>
        <button
          className="button button--ghost"
          disabled={!onPreview || loading}
          onClick={() => load('json')}
        >
          JSON preview
        </button>
      </div>
      {loading ? <p className="empty-copy">Preview wird deterministisch erzeugt…</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
      {preview ? (
        <div className="export-preview-content">
          <small>
            SHA-256 · {preview.sha256} · writes: {String(preview.writesPerformed)}
          </small>
          <pre>{preview.content}</pre>
        </div>
      ) : !loading ? (
        <p className="empty-copy">Format wählen; es wird keine Governance-Datei überschrieben.</p>
      ) : null}
    </Panel>
  );
}

export function GenericSurface({
  title,
  eyebrow,
  description,
}: {
  title: string;
  eyebrow: string;
  description: string;
}) {
  return (
    <Panel className="surface-panel generic-surface">
      <div className="generic-visual">
        <span>
          <CircleGauge size={28} />
        </span>
        <i />
        <i />
        <i />
      </div>
      <div>
        <span className="ui-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        <div className="truth-note">
          <ShieldCheck size={16} />
          <span>
            Runtime mutations are durable and audited. External sends, providers and VPS actions
            remain unavailable; the synthetic operations loop performs no outside write.
          </span>
        </div>
      </div>
    </Panel>
  );
}
